/**
 * The platform's TanStack Query policy — one definition for the main app and
 * every agent sub-app.
 *
 * It was ten copies, none of which registered the client for the identity-
 * boundary cache purge, and they had already diverged on the retry policy:
 * the main app stopped retrying 4xx (a 403 is not transient — retrying it
 * spams the network and delays the error the user needs to see), while every
 * sub-app kept a blanket `retry: 2` and re-asked for permission it had just
 * been refused.
 */
import { QueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { DEFAULT_FRONTEND_CACHE } from '@/lib/query-config'
import { registerQueryClient } from '@/lib/queryClientRef'

export function createAppQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        // app.yaml's `frontend_cache:` default, not a second literal.
        staleTime: DEFAULT_FRONTEND_CACHE.sessions_stale_ms,
        // 4xx are not transient — retry transient/server failures only.
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
          return failureCount < 2
        },
        refetchOnMount: true, // Refetch if data is stale when component mounts
        refetchOnWindowFocus: false, // Don't refetch on tab switch (too aggressive for PWA)
      },
    },
  })
  // The handle stores/auth.ts uses to purge cached PHI at an identity boundary
  // (logout, or a mid-session nurse swap on a shared device). Only the main app
  // ever registered, so that purge was a no-op in all ten sub-apps — the same
  // "nine chances to forget" this module exists to end.
  registerQueryClient(client)
  return client
}
